import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Checkbox,
  Alert,
  Button,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { providersAPI, pricingAPI, vmTypesAPI, regionsAPI } from '../../../services/api';
import { Provider, VMType, Region, VMPricingRow } from '../../../types';

interface PriceChangeMap {
  [pricingId: string]: { monthlyCost?: number; isAvailable?: boolean };
}

interface PriceInputProps {
  pricingId: number;
  value: number;
  onChange: (pricingId: number, field: string, value: number) => void;
}

// Memoized price input component to prevent unnecessary re-renders
const PriceInput = React.memo(({ pricingId, value, onChange }: PriceInputProps) => (
  <TextField
    type="number"
    size="small"
    value={value ?? ''}
    onChange={(e) => {
      const newValue = e.target.value === '' ? 0 : parseFloat(e.target.value);
      onChange(pricingId, 'monthlyCost', newValue);
    }}
    inputProps={{ step: 0.01, min: 0 }}
    sx={{
      width: 100,
      '& .MuiInputBase-input': {
        fontSize: '0.875rem',
        py: 0.5,
        px: 1,
      },
    }}
  />
));

interface AvailabilityCheckboxProps {
  pricingId: number;
  checked: boolean;
  onChange: (pricingId: number, field: string, value: boolean) => void;
}

// Memoized checkbox component
const AvailabilityCheckbox = React.memo(({ pricingId, checked, onChange }: AvailabilityCheckboxProps) => (
  <Checkbox
    checked={checked}
    onChange={(e) => onChange(pricingId, 'isAvailable', e.target.checked)}
  />
));

interface VMSection {
  vm: VMType;
  category: string;
  vmPricing: VMPricingRow[];
}

const VMPricing = () => {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState(0);
  const [vmTypes, setVMTypes] = useState<VMType[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [pricing, setPricing] = useState<VMPricingRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changes, setChanges] = useState<PriceChangeMap>({});
  const [renderedCount, setRenderedCount] = useState(100);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Memoize pricing lookup by VM
  const pricingByVM = useMemo(() => {
    const grouped: Record<number, VMPricingRow[]> = {};
    pricing.forEach(p => {
      if (!grouped[p.VMTypeId]) {
        grouped[p.VMTypeId] = [];
      }
      grouped[p.VMTypeId]!.push(p);
    });
    return grouped;
  }, [pricing]);

  // Create a flattened list of VM + Category combinations for progressive rendering
  const vmSections = useMemo(() => {
    const sections: VMSection[] = [];
    vmTypes.forEach(vm => {
      const vmPricingEntries = pricingByVM[vm.VMTypeId] || [];
      if (vmPricingEntries.length === 0) return;

      const uniqueCategories = [...new Set(vmPricingEntries.map(p => p.OSCategory))].sort();
      if (uniqueCategories.length === 0) return;

      uniqueCategories.forEach(category => {
        sections.push({ vm, category, vmPricing: vmPricingEntries });
      });
    });
    return sections;
  }, [vmTypes, pricingByVM]);

  // Progressive loading effect - load more sections after initial render
  useEffect(() => {
    if (vmSections.length === 0 || renderedCount >= vmSections.length) return;

    setIsLoadingMore(true);
    const timer = setTimeout(() => {
      setRenderedCount(prev => Math.min(prev + 50, vmSections.length));
      setIsLoadingMore(false);
    }, 100);

    return () => clearTimeout(timer);
  }, [vmSections.length, renderedCount]);

  // Reset rendered count when provider changes
  useEffect(() => {
    setRenderedCount(100);
  }, [selectedProvider]);

  const fetchInitialData = async () => {
    try {
      const [providersRes, vmTypesRes] = await Promise.all([
        providersAPI.getAll(),
        vmTypesAPI.getAll(),
      ]);
      setProviders(providersRes.data.filter((p: Provider) => p.IsActive));
      setVMTypes(vmTypesRes.data.filter((v: VMType) => v.IsActive));
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchPricingData = useCallback(async () => {
    if (providers.length === 0) return;

    try {
      const currentProvider = providers[selectedProvider];
      if (!currentProvider) return;
      const [pricingRes, regionsRes] = await Promise.all([
        pricingAPI.getVMPricing(currentProvider.ProviderId),
        regionsAPI.getAll(currentProvider.ProviderId),
      ]);

      setPricing(pricingRes.data);
      setRegions(regionsRes.data.filter((r: Region) => r.IsActive));
      setChanges({});
      setError('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      setError(`Failed to load pricing: ${axiosErr.response?.data?.error || axiosErr.message}`);
    }
  }, [providers, selectedProvider]);

  useEffect(() => {
    fetchInitialData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (providers.length > 0) {
      fetchPricingData();
    }
  }, [selectedProvider, providers, fetchPricingData]);

  const handlePriceChange = (pricingId: number, field: string, value: number | boolean) => {
    setChanges({
      ...changes,
      [pricingId]: {
        ...changes[pricingId],
        [field]: value,
      },
    });
  };

  const handleSaveChanges = async () => {
    try {
      const updates = Object.entries(changes).map(([pricingId, changedFields]) => {
        const originalPricing = pricing.find(p => p.VMPricingId === parseInt(pricingId));
        const mergedData = {
          monthlyCost: changedFields.monthlyCost !== undefined
            ? changedFields.monthlyCost
            : originalPricing?.MonthlyCost ?? 0,
          isAvailable: changedFields.isAvailable !== undefined
            ? changedFields.isAvailable
            : originalPricing?.IsAvailable ?? 0,
        };
        return pricingAPI.updateVMPrice(pricingId, mergedData);
      });

      await Promise.all(updates);
      setSuccess('Prices updated successfully');
      setChanges({});
      fetchPricingData();

      setTimeout(() => setSuccess(''), 3000);
    } catch {
      setError('Failed to update prices');
    }
  };

  const getPriceValue = (pricingId: number, defaultValue: number) => {
    return changes[pricingId]?.monthlyCost !== undefined
      ? changes[pricingId]!.monthlyCost!
      : defaultValue;
  };

  const getAvailableValue = (pricingId: number, defaultValue: number) => {
    return changes[pricingId]?.isAvailable !== undefined
      ? changes[pricingId]!.isAvailable!
      : !!defaultValue;
  };

  const handleToggleAll = (entries: VMPricingRow[], value: boolean) => {
    const newChanges: PriceChangeMap = { ...changes };
    entries.forEach(entry => {
      if (entry) {
        newChanges[entry.VMPricingId] = {
          ...newChanges[entry.VMPricingId],
          isAvailable: value,
        };
      }
    });
    setChanges(newChanges);
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4">VM Pricing</Typography>
          {isLoadingMore && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={12} />
              Loading more... ({renderedCount} of {vmSections.length})
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          color="secondary"
          startIcon={<SaveIcon />}
          onClick={handleSaveChanges}
          disabled={Object.keys(changes).length === 0}
        >
          Save Changes ({Object.keys(changes).length})
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Note:</strong> VM prices now include OS licensing.
          Each VM has separate pricing for Windows and Linux - This price should include the licensing cost for the OS.
        </Typography>
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={selectedProvider}
          onChange={(_e, newValue: number) => setSelectedProvider(newValue)}
          variant="fullWidth"
        >
          {providers.map((provider) => (
            <Tab key={provider.ProviderId} label={provider.ProviderName} />
          ))}
        </Tabs>
      </Paper>

      {vmSections.slice(0, renderedCount).map(({ vm, category, vmPricing: vmPricingEntries }) => {
        const vmOSPricing = vmPricingEntries.filter(p => p.OSCategory === category);

        return (
          <Paper key={`${vm.VMTypeId}-${category}`} sx={{ mb: 3, p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">
                  {vm.VMName} - {vm.CPU} vCPU, {vm.RAM}GB RAM
                </Typography>
                <Chip
                  label={category}
                  size="small"
                  color={category === 'Windows' ? 'primary' : 'success'}
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Button size="small" variant="outlined" color="success" onClick={() => handleToggleAll(vmOSPricing, true)}>Enable All</Button>
                <Button size="small" variant="outlined" color="error" onClick={() => handleToggleAll(vmOSPricing, false)}>Disable All</Button>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Provider</TableCell>
                    {regions.map((region) => (
                      <TableCell key={region.RegionId} align="center">
                        {region.RegionName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Monthly Cost ($)</TableCell>
                    {regions.map((region) => {
                      const priceEntry = vmOSPricing.find(p => p.RegionId === region.RegionId);
                      if (!priceEntry) {
                        return (
                          <TableCell key={region.RegionId} align="center">
                            <Typography variant="caption" color="text.secondary">
                              Not configured
                            </Typography>
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={region.RegionId} align="center">
                          <PriceInput
                            pricingId={priceEntry.VMPricingId}
                            value={getPriceValue(priceEntry.VMPricingId, priceEntry.MonthlyCost)}
                            onChange={handlePriceChange}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    <TableCell>Enabled</TableCell>
                    {regions.map((region) => {
                      const priceEntry = vmOSPricing.find(p => p.RegionId === region.RegionId);
                      if (!priceEntry) return <TableCell key={region.RegionId} />;

                      return (
                        <TableCell key={region.RegionId} align="center">
                          <AvailabilityCheckbox
                            pricingId={priceEntry.VMPricingId}
                            checked={getAvailableValue(priceEntry.VMPricingId, priceEntry.IsAvailable)}
                            onChange={handlePriceChange}
                          />
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        );
      })}

      {isLoadingMore && renderedCount < vmSections.length && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default VMPricing;
